import { UploadWidgetResult } from '@bytescale/upload-widget'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Button, { LinkLikeButton } from 'components/core/button'
import { request } from 'lib/api/fetch-api'
import { AssetableType } from 'lib/api/ticket-files/ticket-files'
import IconButton from 'lib/components/buttons/icon-button'
import { ModalFileUploader } from 'lib/components/file-uploader/file-uploader'
import { ImagePlusIcon, LoaderCircleIcon, PlusIcon, UploadIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

interface ProjectAssetsProps {
  projectId: number
}

export default function ProjectAssets({ projectId }: ProjectAssetsProps) {
  const [isFileUploaderOpen, setIsFileUploaderOpen] = useState(false)
  const queryClient = useQueryClient()
  const {
    data: files,
    isFetching,
    error,
  } = useQuery({
    queryKey: ['projectFiles', projectId],
    queryFn: () => request({ endpoint: 'getProjectFiles', query: { id: projectId } }),
  })

  const createAssetMutation = useMutation({
    mutationFn: ({ filename, s3_filename, mimetype }: { filename: string; s3_filename: string; mimetype: string }) => {
      return request({
        endpoint: 'createAsset',
        body: {
          assetable_type: AssetableType.Project,
          assetable_id: projectId,
          filename,
          s3_filename,
          mimetype,
        },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectFiles', projectId] })
    },
    onError: (error) => {
      console.error('Failed to create asset', error)
      toast.error('Error: Failed to upload asset')
    },
  })

  const postProjectFiles = async (files: UploadWidgetResult[]) => {
    const promises = files.map((file) =>
      createAssetMutation.mutateAsync({
        filename: file.originalFile.originalFileName,
        s3_filename: file.filePath.split('/').pop(),
        mimetype: file.originalFile.mime,
      }),
    )
    await Promise.all(promises)
    setIsFileUploaderOpen(false)
    toast.success('Assets uploaded successfully')
  }

  const Header = () => (
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
      <h5>Assets ({files?.meta?.total || 0})</h5>
      <IconButton color="secondary" onClick={() => setIsFileUploaderOpen(true)}>
        <PlusIcon className="lu-md" />
      </IconButton>
    </div>
  )

  const EmptyState = () => (
    <div className="tw-border-1 tw-flex tw-h-full tw-min-h-48 tw-w-full tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border-solid tw-border-peppercorn-100">
      <div className="tw-rounded-s tw-bg-neutral-50 tw-p-2">
        <ImagePlusIcon className="lu-xl tw-text-neutral-500" />
      </div>
      <div className="tw-text-center tw-text-base tw-font-bold tw-text-neutral-800">
        You don&apos;t have any assets yet.
      </div>
      <div className="tw-max-w-prose tw-text-center tw-text-neutral-500">
        Upload must-have elements for your designer, or pull in existing assets from your other requests, projects or
        brand profiles.
      </div>
      <div className="tw-flex tw-gap-2">
        <Button color="neutral" className="tw-flex tw-items-center tw-gap-2">
          <PlusIcon className="lu-md" />
          Add local assets
        </Button>
        <Button
          color="neutral"
          className="tw-flex tw-items-center tw-gap-2"
          onClick={() => setIsFileUploaderOpen(true)}
        >
          <UploadIcon className="lu-md" />
          Upload assets
        </Button>
      </div>
    </div>
  )

  useEffect(() => {
    if (error) {
      console.error('Failed to fetch project assets', error)
      toast.error('Error: Failed to fetch project assets')
    }
  }, [error])

  if (isFetching) {
    return <LoaderCircleIcon className="lu-md tw-animate-spin" />
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <Header />
      {files?.data?.length === 0 ? <EmptyState /> : <div>Files</div>}
      {files?.data?.length > 0 && (
        <div>
          <h5>Files</h5>
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
            <div className="tw-flex tw-h-28 tw-w-full tw-flex-wrap tw-gap-2 tw-overflow-hidden tw-p-2">
              {files?.data?.map((file) => (
                <img
                  className="tw-aspect-square tw-h-full tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-neutral-100 tw-object-cover"
                  key={file.id}
                  src={file.url}
                  alt={file.name}
                />
              ))}
            </div>
            <LinkLikeButton onClick={() => {}} className="tw-shrink-0">
              See All
            </LinkLikeButton>
          </div>
        </div>
      )}
      <ModalFileUploader isOpen={isFileUploaderOpen} setIsOpen={setIsFileUploaderOpen} onComplete={postProjectFiles} />
    </div>
  )
}
